import * as Sentry from "@sentry/react";
import { useConvex, useQuery } from "convex/react";
import { GripVertical, Star } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { EditorHeader } from "@/components/editor/EditorHeader";
import { ExportDialog } from "@/components/editor/ExportDialog";
import { SceneHierarchy } from "@/components/editor/SceneHierarchy";
import { VerticalNav } from "@/components/editor/VerticalNav";
import Viewer360 from "@/components/editor/Viewer360";
import ZoomableImageViewer from "@/components/editor/ZoomableImageViewer";
import { ComponentLibrary } from "@/components/skin/ComponentLibrary";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  createPanorama as createLocalPanorama,
  useHotspotsByProject,
  usePanoramasByProject,
} from "@/data";
import { createSnapshot as createLocalSnapshot } from "@/data/adapters/local/projectSnapshots";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useIndexedDBCache } from "@/hooks/useIndexedDBCache";
import { useS3Storage } from "@/hooks/useS3Storage";
import { allCraftspacesDb, localDb } from "@/lib/localDb";
import { updatePanorama as updateLocalPanorama } from "@/data/adapters/local/panoramas";
import { isElectron, LocalFileSystemService } from "@/services/storage/LocalFileSystemClient";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { StorageService } from "@/services/storageService";
import type { Panorama } from "@/store/editorStore";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";
import type { Tables } from "@/types/database";
import { StoragePaths, resolveStoragePathSegments } from "@/utils/storagePaths";
import { getPanoramaMediaType, setPanoramaMediaType } from "@/utils/panoramaMedia";
import { api } from "../../convex/_generated/api";
import { BREAKPOINT_PRESETS } from "@/lib/breakpoints";
import { ViewportControlsProvider } from "@/components/editor/ViewportControlsContext";

interface SkinComponent {
  id: string;
  type: "box" | "text";
  name: string;
  position: { x: number; y: number };
  size: { width: number | "auto"; height: number | "auto"; aspectRatio?: string };
  style: Record<string, unknown>;
  content: Record<string, unknown>;
  visible: boolean;
  parentId?: string;
  zIndex: number;
  pointerEvents?: boolean;
  hideOnStart?: boolean;
}

type Hotspot = Tables<"hotspots">;

const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );

const ProjectCraftspace = () => {
  const showBreakpointControls = false;
  const { projectId } = useParams<{ projectId: string }>();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const convex = useConvex();
  const projectBackend = useEditorStore((state) => state.projectBackend);
  const selectedPanorama = useEditorStore((state) => state.selectedPanorama);

  // Real-time subscription to panoramas for this project (Convex — skipped in Electron)
  const livePanoramas = useQuery(
    api.panoramas.listByProject,
    projectBackend === "cloud" && projectId ? { projectId } : "skip",
  ) as Panorama[] | undefined;

  const location = useLocation();
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  const convexRef = useRef(convex);
  const isSettingsRoute = location.pathname.includes("/settings");
  const isTrashRoute = location.pathname.includes("/trash");
  const isSkinRoute = location.pathname.includes("/skin");
  const isEditorRoute =
    location.pathname.includes("/editor") || location.pathname === `/${projectId}`;
  const isMapsRoute = location.pathname.includes("/maps");
  const isCMSRoute = location.pathname.includes("/cms");
  const isVariablesRoute = location.pathname.includes("/variables");
  const isAssetsRoute = location.pathname.includes("/assets");

  const {
    setProject,
    setPanoramas,
    setHotspots,
    setSelectedPanorama,
    setSelectedHotspot,
    clearHotspotSelection,
    setLoading,
    project,
    loading,
    hotspots,
    panoramas,
    selectedSkinComponent,
    setSelectedSkinComponent,
    skinComponents,
    setSkinComponents,
    localLoading,
    setLocalLoading,
    addSkinComponent,
    updateSkinComponent,
    deleteSkinComponent,
    reorderSkinComponents,
    nestSkinComponent,
    setEnableHotspotPlacement,
    setEnableHotspotEditing,
    setEnableSkinComponentEditing,
    setShowPlacementButton,
    setCurrentRoute,
    setViewerVisible,
    setIsPanelSwitching,
    setProjectBackend,
    isPreviewMode,
  } = useEditorStore();

  // Local SQLite reactive subscriptions (only used in Electron)
  // Must be after useEditorStore so `project` is available
  const localCraftspaceId = projectBackend === "local" && isElectron() ? (project?.craftspace_id ?? undefined) : undefined;
  const localPanoramas = usePanoramasByProject(
    projectBackend === "local" && isElectron() ? projectId : undefined,
    localCraftspaceId,
  );
  const localHotspots = useHotspotsByProject(
    projectBackend === "local" && isElectron() ? projectId : undefined,
    localCraftspaceId,
  );

  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [backupName, setBackupName] = useState("");
  const [backupSaving, setBackupSaving] = useState(false);

  const useLocalBackend =
    isElectron() && (projectBackend === "local" || (project?.id && isUuid(project.id)));
  const projectData = project?.data as Record<string, unknown> | null;
  const storageSegments = project
    ? resolveStoragePathSegments({
        craftspaceId: project.craftspace_id || "default",
        projectId: project.id,
        projectName: project.name,
        projectData,
        userEmail: user?.email,
        userId: user?.id,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      })
    : { craftspaceKey: "default", projectKey: "default" };

  const storeActionsRef = useRef({
    setProject,
    setPanoramas,
    setHotspots,
    setSelectedPanorama,
    setSelectedHotspot,
    setSelectedSkinComponent,
    setSkinComponents,
    setLoading,
    setLocalLoading,
    setProjectBackend,
  });

  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email });
    } else {
      Sentry.setUser(null);
    }

    Sentry.setTag("project_id", projectId ?? "unknown");
    Sentry.setTag("craftspace_id", project?.craftspace_id ?? "unknown");
    if (project) {
      Sentry.setContext("project", {
        id: project.id,
        name: project.name,
        craftspace_id: project.craftspace_id ?? "unknown",
      });
    } else {
      Sentry.setContext("project", null);
    }
  }, [project, projectId, user]);

  // Electron: sync local SQLite panoramas into the editor store reactively
  useEffect(() => {
    if (projectBackend !== "local" || localPanoramas === undefined) return;
    const mapped = localPanoramas.map((lp) => ({
      ...lp,
      id: lp._id,
    })) as unknown as Panorama[];
    setPanoramas(mapped);
    const { selectedPanorama } = useEditorStore.getState();
    if (!selectedPanorama && mapped.length > 0) {
      setSelectedPanorama(mapped[0]);
    } else if (selectedPanorama) {
      const refreshed = mapped.find((p) => p.id === selectedPanorama.id);
      // Use setState directly to refresh selectedPanorama without resetting selectedPanoramaIds
      if (refreshed) useEditorStore.setState({ selectedPanorama: refreshed });
    }
  }, [localPanoramas, projectBackend, setPanoramas, setSelectedPanorama]);

  // Electron: sync local SQLite hotspots into the editor store reactively
  useEffect(() => {
    if (projectBackend !== "local" || localHotspots === undefined) return;
    const mapped = localHotspots.map((lh) => {
      // SQL returns position_x/y/z as separate columns; reconstruct the position
      // object that HotspotManager.getHotspotPosition() expects.
      const raw = lh as unknown as Record<string, unknown>;
      return {
        ...lh,
        id: lh._id,
        position: {
          x: (raw.position_x as number) ?? 0,
          y: (raw.position_y as number) ?? 0,
          z: (raw.position_z as number) ?? 0,
        },
        rotation: {
          x: (raw.rotation_x as number) ?? 0,
          y: (raw.rotation_y as number) ?? 0,
          z: (raw.rotation_z as number) ?? 0,
        },
        scale: {
          x: (raw.scale_x as number) ?? 1,
          y: (raw.scale_y as number) ?? 1,
          z: (raw.scale_z as number) ?? 1,
        },
        visible:
          raw.visible === undefined ? true : Boolean(raw.visible),
      };
    }) as unknown as Hotspot[];
    setHotspots(mapped);
  }, [localHotspots, projectBackend, setHotspots]);

  // Sync live panorama subscription into the store after initial load completes (Convex only)
  useEffect(() => {
    if (projectBackend !== "cloud" || localLoading || loading) return;
    if (!livePanoramas) return;

    const currentPanoramas = useEditorStore.getState().panoramas;
    const currentMap = new Map(currentPanoramas.map((p) => [p.id, p]));
    const liveIds = new Set(livePanoramas.map((p) => p.id));

    const hasNewPanoramas = livePanoramas.some((p) => !currentMap.has(p.id));
    const hasRemovedPanoramas = currentPanoramas.some((p) => !liveIds.has(p.id));

    // Also detect updates to existing panoramas (e.g. thumbnail added, processing_status changed)
    const hasUpdatedPanoramas = livePanoramas.some((liveP) => {
      const current = currentMap.get(liveP.id);
      if (!current) return false;
      return (
        current.thumbnail_url !== liveP.thumbnail_url ||
        current.thumbnail_url_webp !== liveP.thumbnail_url_webp ||
        current.processing_status !== liveP.processing_status
      );
    });

    if (hasNewPanoramas || hasRemovedPanoramas || hasUpdatedPanoramas) {
      setPanoramas(livePanoramas);
      // If there's no selected panorama but we now have some, select the first
      const { selectedPanorama } = useEditorStore.getState();
      if (!selectedPanorama && livePanoramas.length > 0) {
        setSelectedPanorama(livePanoramas[0]);
      }
    }
  }, [livePanoramas, localLoading, loading, projectBackend, setPanoramas, setSelectedPanorama]);

  // Initialize S3 storage hook for the craftspace
  const craftspaceId = project?.craftspace_id || "default";
  const {
    uploadFile: s3UploadFile,
    getFileUrl: s3GetFileUrl,
    initializeStorage: s3InitializeStorage,
  } = useS3Storage({ craftspaceId });

  // Reinitialize S3 storage when craftspace changes
  const [s3InitializationInProgress, setS3InitializationInProgress] = useState(false);
  const [lastInitializedCraftspace, setLastInitializedCraftspace] = useState<string | null>(null);
  const [viewerWidth, setViewerWidth] = useState<number | null>(null);
  const [viewportMode, setViewportMode] = useState<"auto" | "fixed">("auto");
  const [isBreakpointHandleHovered, setIsBreakpointHandleHovered] = useState(false);
  const [isBreakpointHandleDragging, setIsBreakpointHandleDragging] = useState(false);
  const [hasAttemptedProjectLoad, setHasAttemptedProjectLoad] = useState(false);
  const [windowWidth, setWindowWidth] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1400,
  );

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    convexRef.current = convex;
  }, [convex]);

  useEffect(() => {
    storeActionsRef.current = {
      setProject,
      setPanoramas,
      setHotspots,
      setSelectedPanorama,
      setSelectedHotspot,
      setSelectedSkinComponent,
      setSkinComponents,
      setLoading,
      setLocalLoading,
      setProjectBackend,
    };
  }, [
    setProject,
    setPanoramas,
    setHotspots,
    setSelectedPanorama,
    setSelectedHotspot,
    setSelectedSkinComponent,
    setSkinComponents,
    setLoading,
    setLocalLoading,
    setProjectBackend,
  ]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tag = target.tagName.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return true;
      return target.isContentEditable;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isPreviewMode) return;
      if (event.defaultPrevented) return;
      if (isEditableTarget(event.target)) return;

      const key = event.key.toLowerCase();
      const isUndo = (event.metaKey || event.ctrlKey) && !event.shiftKey && key === "z";
      const isRedo =
        (event.metaKey || event.ctrlKey) && (key === "y" || (event.shiftKey && key === "z"));
      const isDelete = event.key === "Delete" || event.key === "Backspace";
      const isBackup = (event.metaKey || event.ctrlKey) && event.shiftKey && key === "s";

      if (isUndo) {
        event.preventDefault();
        useEditorStore
          .getState()
          .undoHotspotEdit()
          .catch((error) => console.error("Undo failed:", error));
      }

      if (isRedo) {
        event.preventDefault();
        useEditorStore
          .getState()
          .redoHotspotEdit()
          .catch((error) => console.error("Redo failed:", error));
      }

      if (isDelete && selectedSkinComponent) {
        event.preventDefault();
        deleteSkinComponent(selectedSkinComponent.id);
      }

      if (isBackup) {
        event.preventDefault();
        const timestamp = new Date().toLocaleString();
        setBackupName(`Backup ${timestamp}`);
        setBackupDialogOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewMode, selectedSkinComponent, deleteSkinComponent]);

  useEffect(() => {
    if (
      craftspaceId &&
      craftspaceId !== "default" &&
      !s3InitializationInProgress &&
      lastInitializedCraftspace !== craftspaceId
    ) {
      setS3InitializationInProgress(true);

      // Check the current storage provider
      StorageServiceFactory.getCraftspaceStorageProvider(craftspaceId)
        .then((provider) => {
          if (provider === "custom-s3") {
            // Check if we have stored credentials
            StorageServiceFactory.hasStoredCredentials(craftspaceId)
              .then((hasCreds) => {
                if (hasCreds) {
                  // Try to initialize the storage service
                  s3InitializeStorage()
                    .then((_service) => {
                      setLastInitializedCraftspace(craftspaceId);
                    })
                    .catch((error) => {
                      console.error("S3 storage initialization failed:", error);
                    })
                    .finally(() => {
                      setS3InitializationInProgress(false);
                    });
                } else {
                  setS3InitializationInProgress(false);
                }
              })
              .catch((error) => {
                console.error("Error checking stored credentials:", error);
                setS3InitializationInProgress(false);
              });
          } else {
            setS3InitializationInProgress(false);
          }
        })
        .catch((error) => {
          console.error("Error getting storage provider:", error);
          setS3InitializationInProgress(false);
        });
    }
  }, [craftspaceId, s3InitializeStorage, s3InitializationInProgress, lastInitializedCraftspace]);

  // Initialize IndexedDB cache
  const { cachePanorama, cacheThumbnail, isInitialized: isCacheInitialized } = useIndexedDBCache();

  // State to track pending uploads
  const [_pendingUploads, _setPendingUploads] = useState<FileList | null>(null);

  // State for export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // Refresh function to reload panorama list from storage
  const handleRefresh = useCallback(async () => {
    if (!projectId || !user || !project) return;

    try {
      setLoading(true);

      // Use storage discovery to find panoramas
      const { discoverPanoramasFromStorage } = useEditorStore.getState();
      // Discover panoramas from storage
      const discoveredPanoramas = await discoverPanoramasFromStorage(
        storageSegments.craftspaceKey,
        storageSegments.projectKey,
      );

      // Sync discovered panoramas with database
      const { syncDiscoveredPanoramas } = useEditorStore.getState();
      await syncDiscoveredPanoramas(storageSegments.craftspaceKey, storageSegments.projectKey);

      // Also fetch hotspots from database
      const hotspotsData = (await convex.query(api.hotspots.listByProject, {
        projectId,
      })) as Hotspot[];
      setHotspots(hotspotsData || []);

      toast({
        title: "Refreshed",
        description: `Found ${discoveredPanoramas.length} panoramas in storage`,
      });
    } catch (error) {
      console.error("Refresh error:", error);
      toast({
        variant: "destructive",
        title: "Refresh failed",
        description: "Failed to refresh project data",
      });
    } finally {
      setLoading(false);
    }
  }, [convex, projectId, user, project, storageSegments, setLoading, setHotspots, toast]);

  const buildSnapshotData = useCallback(() => {
    const state = useEditorStore.getState();
    const varsState = useVariablesStore.getState();
    const currentProject = state.project;
    const projectVars = currentProject?.id
      ? varsState.projectVariables[currentProject.id]
      : undefined;
    const craftspaceVars = currentProject?.craftspace_id
      ? varsState.craftspaceVariables[currentProject.craftspace_id]
      : undefined;

    const baseData =
      currentProject?.data && typeof currentProject.data === "object"
        ? (currentProject.data as Record<string, unknown>)
        : {};
    const mergedProjectData = {
      ...baseData,
      skinComponents: state.skinComponents,
    };

    return {
      schemaVersion: 1,
      capturedAt: new Date().toISOString(),
      project: currentProject
        ? {
            name: currentProject.name,
            description: currentProject.description ?? undefined,
            data: mergedProjectData,
          }
        : undefined,
      panoramas: state.panoramas.map((p) => ({
        id: p.id,
        name: p.name,
        order: p.order,
        data: p.data,
        gps_location: p.gps_location,
      })),
      hotspots: state.hotspots.map((h) => ({
        id: h.id,
        panorama_id: h.panorama_id,
        name: h.name,
        type: h.type,
        content: h.content,
        position: h.position,
        rotation: h.rotation,
        scale: h.scale,
        visible: h.visible,
        order: h.order,
      })),
      variables: {
        projectVariables: projectVars,
        craftspaceVariables: craftspaceVars,
      },
    };
  }, []);

  const handleCreateBackup = useCallback(async () => {
    if (!project) return;
    const trimmed = backupName.trim();
    if (!trimmed) {
      toast({ variant: "destructive", title: "Name required", description: "Enter a backup name." });
      return;
    }

    setBackupSaving(true);
    try {
      const snapshotData = buildSnapshotData();
      if (useLocalBackend) {
        await createLocalSnapshot({
          project_id: project.id,
          craftspace_id: project.craftspace_id || "",
          name: trimmed,
          trigger: "manual",
          snapshot_data: snapshotData,
        });
      } else {
        await convex.mutation(api.projectSnapshots.create, {
          project_id: project.id as any,
          name: trimmed,
          trigger: "manual",
          snapshot_data: snapshotData,
        });
      }
      toast({ title: "Backup saved", description: `"${trimmed}" created.` });
      setBackupDialogOpen(false);
    } catch (error) {
      console.error("Backup creation failed:", error);
      toast({
        variant: "destructive",
        title: "Backup failed",
        description: "Could not create backup.",
      });
    } finally {
      setBackupSaving(false);
    }
  }, [backupName, buildSnapshotData, convex, project, toast, useLocalBackend]);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const resizerRef = useRef<HTMLDivElement>(null);

  const getViewerBounds = useCallback(() => {
    const parentWidth = viewerContainerRef.current?.parentElement?.offsetWidth;
    const preferredMinWidth = isSkinRoute ? 320 : windowWidth < 1280 ? 200 : 220;
    const minWidth = Math.min(preferredMinWidth, parentWidth ?? preferredMinWidth);
    const maxWidth = Math.max(minWidth, parentWidth ?? window.innerWidth);
    return { minWidth, maxWidth };
  }, [isSkinRoute, windowWidth]);

  const clampViewerWidth = useCallback(
    (width: number) => {
      const { minWidth, maxWidth } = getViewerBounds();
      return Math.min(Math.max(width, minWidth), maxWidth);
    },
    [getViewerBounds],
  );

  const dispatchViewerResize = useCallback((container: HTMLDivElement, startAspect?: number) => {
    const viewer = container.querySelector("canvas");
    if (!viewer) return;

    const safeStartAspect = startAspect ?? viewer.clientWidth / Math.max(viewer.clientHeight, 1);
    const newAspect = viewer.clientWidth / Math.max(viewer.clientHeight, 1);

    window.dispatchEvent(
      new CustomEvent("viewer-resize", {
        detail: { startAspect: safeStartAspect, newAspect },
      }),
    );
  }, []);

  const applyViewerWidth = useCallback(
    (width: number, options?: { persist?: boolean }) => {
      const container = viewerContainerRef.current;
      if (!container) return;

      const clampedWidth = clampViewerWidth(width);
      const viewer = container.querySelector("canvas");
      const startAspect = viewer ? viewer.clientWidth / Math.max(viewer.clientHeight, 1) : 1;

      container.style.width = `${clampedWidth}px`;
      if (options?.persist !== false) {
        setViewerWidth(clampedWidth);
      }

      requestAnimationFrame(() => {
        dispatchViewerResize(container, startAspect);
      });
    },
    [clampViewerWidth, dispatchViewerResize],
  );

  const setViewportAuto = useCallback(() => {
    const container = viewerContainerRef.current;
    setViewportMode("auto");
    setViewerWidth(null);
    if (!container) return;
    container.style.width = "100%";
    requestAnimationFrame(() => {
      dispatchViewerResize(container);
    });
  }, [dispatchViewerResize]);

  const setViewportFixedWidth = useCallback(
    (width: number) => {
      setViewportMode("fixed");
      applyViewerWidth(width);
    },
    [applyViewerWidth],
  );

  // Cleanup resizer observers when component unmounts or resizer changes
  useEffect(() => {
    return () => {
      if (resizerRef.current?._cleanup) {
        resizerRef.current._cleanup();
      }
    };
  }, []);

  // Handle preview mode changes
  useEffect(() => {
    const container = viewerContainerRef.current;
    if (!container) return;

    // Wait one frame for preview layout changes (panel hide/show) to settle before resizing renderer.
    const frameId = requestAnimationFrame(() => {
      const viewer = container.querySelector("canvas");
      if (!viewer) return;

      const startAspect = viewer.clientWidth / viewer.clientHeight;
      const newAspect = container.clientWidth / Math.max(container.clientHeight, 1);
      window.dispatchEvent(
        new CustomEvent("viewer-resize", {
          detail: { startAspect, newAspect, previewMode: isPreviewMode },
        }),
      );
    });

    return () => cancelAnimationFrame(frameId);
  }, [isPreviewMode]);

  // Initialize viewer width on mount
  useEffect(() => {
    if (isPreviewMode || !viewerContainerRef.current) return;

    const container = viewerContainerRef.current;
    if ((isSkinRoute || isEditorRoute) && viewportMode === "auto") {
      container.style.width = "100%";
      requestAnimationFrame(() => {
        dispatchViewerResize(container);
      });
      return;
    }

    if (viewerWidth === null) {
      const parentWidth = container.parentElement?.offsetWidth || 0;
      const initialWidth = Math.max(parentWidth * 0.8, 400);
      setViewportMode("fixed");
      applyViewerWidth(initialWidth);
    }
  }, [
    isPreviewMode,
    viewerWidth,
    applyViewerWidth,
    dispatchViewerResize,
    isSkinRoute,
    isEditorRoute,
    viewportMode,
  ]);

  // Handle window resize events for viewer container
  useEffect(() => {
    const container = viewerContainerRef.current;
    if (!container) return;

    const handleResize = () => {
      if (!isPreviewMode && (isSkinRoute || isEditorRoute) && viewportMode === "auto") {
        container.style.width = "100%";
        dispatchViewerResize(container);
        return;
      }

      if (!isPreviewMode) {
        applyViewerWidth(container.offsetWidth);
        return;
      }

      dispatchViewerResize(container);
    };

    window.addEventListener("resize", handleResize);

    // Cleanup function
    return () => window.removeEventListener("resize", handleResize);
  }, [isPreviewMode, applyViewerWidth, dispatchViewerResize, isSkinRoute, isEditorRoute, viewportMode]);

  // Set viewer feature flags based on current route
  useEffect(() => {
    // Show brief loading indicator when switching panels
    setIsPanelSwitching(true);

    const timer = setTimeout(() => {
      setIsPanelSwitching(false);
    }, 150); // Brief 150ms loading state

    if (isSettingsRoute) {
      setCurrentRoute("settings");
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
      setViewerVisible(false);
      setSelectedSkinComponent(null);
    } else if (isTrashRoute) {
      setCurrentRoute("trash");
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
      setViewerVisible(false);
      setSelectedSkinComponent(null);
    } else if (isCMSRoute) {
      setCurrentRoute("cms");
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
      setViewerVisible(false);
      setSelectedSkinComponent(null);
    } else if (isMapsRoute) {
      setCurrentRoute("editor"); // Use editor as default route type
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
      setViewerVisible(false);
      setSelectedSkinComponent(null);
    } else if (isAssetsRoute) {
      setCurrentRoute("assets");
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
      setViewerVisible(false);
      setSelectedSkinComponent(null);
    } else if (isVariablesRoute) {
      setCurrentRoute("variables");
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
      setViewerVisible(false);
      setSelectedSkinComponent(null);
    } else if (isSkinRoute) {
      setCurrentRoute("skin");
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(!isPreviewMode);
      setShowPlacementButton(false);
      setViewerVisible(true);
      setSelectedHotspot(null);
      clearHotspotSelection();
    } else {
      setCurrentRoute("editor");
      setEnableHotspotPlacement(!isPreviewMode);
      setEnableHotspotEditing(!isPreviewMode);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(!isPreviewMode);
      setViewerVisible(true);
      setSelectedSkinComponent(null);
    }

    return () => clearTimeout(timer);
  }, [
    isSkinRoute,
    isSettingsRoute,
    isTrashRoute,
    isMapsRoute,
    isCMSRoute,
    isVariablesRoute,
    isAssetsRoute,
    isPreviewMode,
    setCurrentRoute,
    setEnableHotspotPlacement,
    setEnableHotspotEditing,
    setEnableSkinComponentEditing,
    setShowPlacementButton,
    setSelectedSkinComponent,
    setSelectedHotspot,
    clearHotspotSelection,
    setViewerVisible,
    setIsPanelSwitching,
  ]);

  // Update viewer flags when preview mode changes
  useEffect(() => {
    if (isPreviewMode) {
      setEnableHotspotPlacement(false);
      setEnableHotspotEditing(false);
      setEnableSkinComponentEditing(false);
      setShowPlacementButton(false);
    } else {
      // Re-apply route-specific settings when exiting preview mode
      if (isSettingsRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(false);
      } else if (isTrashRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(false);
      } else if (isCMSRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(false);
      } else if (isMapsRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(false);
      } else if (isAssetsRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(false);
      } else if (isVariablesRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(false);
      } else if (isSkinRoute) {
        setEnableHotspotPlacement(false);
        setEnableHotspotEditing(false);
        setEnableSkinComponentEditing(true);
        setShowPlacementButton(false);
      } else {
        setEnableHotspotPlacement(true);
        setEnableHotspotEditing(true);
        setEnableSkinComponentEditing(false);
        setShowPlacementButton(true);
      }
    }
  }, [
    isPreviewMode,
    isSettingsRoute,
    isTrashRoute,
    isMapsRoute,
    isCMSRoute,
    isVariablesRoute,
    isAssetsRoute,
    isSkinRoute,
    setEnableHotspotPlacement,
    setEnableHotspotEditing,
    setEnableSkinComponentEditing,
    setShowPlacementButton,
  ]);

  useEffect(() => {
    const fetchProjectData = async () => {
      if (!projectId) return;
      const {
        setProject,
        setPanoramas,
        setHotspots,
        setSelectedPanorama,
        setSelectedHotspot,
        setSelectedSkinComponent,
        setSkinComponents,
        setLoading,
        setLocalLoading,
        setProjectBackend,
      } = storeActionsRef.current;
      setHasAttemptedProjectLoad(false);
      let safeHotspotsPromise: Promise<Hotspot[]> | null = null;

      // Clear previous project data only when switching to a different project.
      // Re-running due to auth loading (user?.id changing) should NOT wipe
      // panoramas that were already synced from SQLite, because the reactive
      // sync effect won't re-fire if localPanoramas hasn't changed.
      const currentProjectId = useEditorStore.getState().project?.id;
      if (currentProjectId !== projectId) {
        setProject(null);
        setPanoramas([]);
        setHotspots([]);
        setSkinComponents([]);
        setSelectedPanorama(null);
        setSelectedHotspot(null);
        setSelectedSkinComponent(null);
      }

      // Note: Texture cache clearing removed with old viewer implementation
      // New viewer handles texture cleanup automatically

      setLoading(true);
      setLocalLoading(true);
      try {
        if (isElectron()) {
          // In Electron, local projects use SQLite; cloud projects fall back to Convex.
          const rows = await allCraftspacesDb.query<{
            id: string;
            name: string;
            craftspace_id: string;
            data: string | null;
            created_at: string | null;
            updated_at: string | null;
          }>("SELECT id, name, craftspace_id, data, created_at, updated_at FROM projects WHERE id = ?", [projectId]);
          const row = rows[0];
          if (row) {
            const projectData = {
              id: row.id,
              name: row.name,
              craftspace_id: row.craftspace_id,
              data: row.data ? JSON.parse(row.data) : null,
              created_at: row.created_at ?? undefined,
              updated_at: row.updated_at ?? undefined,
            } as unknown as Tables<"projects">;
            setProjectBackend("local");
            setProject(projectData);
            if (projectData.data) {
              const savedComponents =
                ((projectData.data as Record<string, unknown>)
                  ?.skinComponents as SkinComponent[]) || [];
              setSkinComponents(savedComponents);
            } else {
              setSkinComponents([]);
            }
            return;
          }
        }

        if (!user) {
          throw new Error("Authentication required for cloud project");
        }

        setProjectBackend("cloud");

        // Ensure project has a public short ID, then fetch canonical project data.
        const projectData = await convexRef.current.mutation(api.projects.ensurePublicId, {
          id: projectId,
        });
        if (!projectData) throw new Error("Project not found");

        // Canonicalize old long-ID routes to short prj... routes.
        if (typeof projectData.id === "string" && projectData.id !== projectId) {
          const pathSegments = locationRef.current.pathname.split("/").filter(Boolean);
          if (pathSegments.length > 0 && pathSegments[0] === projectId) {
            pathSegments[0] = projectData.id;
          } else {
            pathSegments.unshift(projectData.id);
          }

          navigateRef.current(
            `/${pathSegments.join("/")}${locationRef.current.search}${locationRef.current.hash}`,
            {
              replace: true,
            },
          );
          return;
        }

        setProject(projectData);

        // Load skin components from project data
        if (projectData.data) {
          const projectDataTyped = projectData.data as Record<string, unknown>;
          const savedComponents = (projectDataTyped?.skinComponents as SkinComponent[]) || [];
          setSkinComponents(savedComponents);
        } else {
          setSkinComponents([]);
        }

        // Fetch core project payloads in parallel so the viewer starts sooner.
        const panoramasPromise = convexRef.current.query(api.panoramas.listByProject, {
          projectId: projectData.id,
        });
        safeHotspotsPromise = convexRef.current
          .query(api.hotspots.listByProject, {
            projectId: projectData.id,
          })
          .catch(() => []);

        const panoramasData = (await panoramasPromise) as Panorama[];

        // Set panoramas immediately so the viewer can start loading without waiting for URL refresh
        if (panoramasData && panoramasData.length > 0) {
          setPanoramas(panoramasData);
          setSelectedPanorama(panoramasData[0]);

          // Refresh expired URLs in the background — viewer will retry on its own if a URL is stale
          (async () => {
            try {
              const { refreshProjectUrls } = await import("@/utils/refreshPrivateUrls");
              const refreshPromise = refreshProjectUrls(projectData.id);
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error("URL refresh timeout")), 10000),
              );
              const refreshResult = (await Promise.race([refreshPromise, timeoutPromise])) as
                | { success?: boolean; refreshedCount?: number }
                | undefined;

              if (refreshResult.success && refreshResult.refreshedCount > 0) {
                // Re-fetch panoramas with fresh URLs and update the store
                const refreshedPanoramas = (await convexRef.current.query(
                  api.panoramas.listByProject,
                  {
                    projectId: projectData.id,
                  },
                )) as Panorama[];
                if (refreshedPanoramas) {
                  setPanoramas(refreshedPanoramas);
                }
              }
            } catch {
              // Silently ignore — viewer handles stale URLs with its own retry logic
            }
          })();
        } else {
          setPanoramas([]);
          setSelectedPanorama(null);
        }

        // Reset hotspot selection/state before we drop the loading screen.
        setSelectedHotspot(null);
        setHotspots([]);

        // Continue fetching secondary payloads in the background to unblock first paint.
        void (async () => {
          const hotspotsData = (await safeHotspotsPromise) as Hotspot[];
          setHotspots(hotspotsData || []);
        })();

        // Stop showing the full-page project loading UI as soon as panoramas are ready.
        setLoading(false);
      } catch (_err) {
        if (safeHotspotsPromise) {
          await safeHotspotsPromise;
        }
        setProject(null);
        setProjectBackend(null);
      } finally {
        setHasAttemptedProjectLoad(true);
        setLoading(false);
        setLocalLoading(false);
      }
    };
    fetchProjectData();
  }, [
    projectId,
    user?.id,
  ]);

  // Listen for editor refresh requests (e.g., after panorama restoration)
  useEffect(() => {
    const handleEditorRefresh = () => {
      // console.log('🔄 Editor refresh requested, refreshing panorama list...');
      handleRefresh();
    };

    window.addEventListener("editor-refresh-needed", handleEditorRefresh);

    return () => {
      window.removeEventListener("editor-refresh-needed", handleEditorRefresh);
    };
  }, [handleRefresh]);

  // Upload panoramas implementation
  const handleUploadPanoramas = async (
    files: FileList,
    uploadPreference?: "original-only" | "compressed-only",
    mediaType: "panorama" | "zoomable_image" = "panorama",
  ) => {
    if (!project) return;
    if (!user && projectBackend !== "local") return;

    // Get or create craftspace ID
    let craftspaceId = project.craftspace_id;

    if (!craftspaceId) {
      if (projectBackend === "local") {
        craftspaceId = "default";
      } else {
        // Create a default craftspace for this user if none exists
        try {
          const ensuredCraftspace = await convex.mutation(
            api.craftspaces.createDefaultCraftspaceIfMissing,
            {},
          );
          craftspaceId = ensuredCraftspace?.id;

          if (craftspaceId) {
            await convex.mutation(api.projects.update, {
              id: project.id,
              craftspace_id: craftspaceId,
            });
            setProject({ ...project, craftspace_id: craftspaceId });
          } else {
            craftspaceId = "default";
          }
        } catch (error) {
          console.error("Error in craftspace creation:", error);
          craftspaceId = "default";
        }
      }
    }
    const finalCraftspaceId = craftspaceId || "default";

    try {
      const preloadImage = (url: string, timeoutMs = 12000): Promise<void> =>
        new Promise((resolve, reject) => {
          const img = new Image();
          const timeout = window.setTimeout(() => {
            img.onload = null;
            img.onerror = null;
            reject(new Error(`Image load timeout: ${url}`));
          }, timeoutMs);
          img.onload = () => {
            clearTimeout(timeout);
            resolve();
          };
          img.onerror = () => {
            clearTimeout(timeout);
            reject(new Error(`Image load failed: ${url}`));
          };
          img.src = url;
        });

      const fileArray = Array.from(files);

      // ── Electron local project: import panoramas to local filesystem ────────
      if (projectBackend === "local") {
        const localStorageSvc = new LocalFileSystemService();
        const existingRows = await localDb(project.craftspace_id || "").query<{ id: string }>(
          "SELECT id FROM panoramas WHERE project_id = ?",
          [project.id],
        );
        const baseOrder = existingRows.length;

        for (let fileIndex = 0; fileIndex < fileArray.length; fileIndex++) {
          const file = fileArray[fileIndex];
          if (!file.type.startsWith("image/")) {
            toast({
              variant: "destructive",
              title: "Invalid file type",
              description: `${file.name} is not an image file.`,
            });
            continue;
          }

          const timestamp = Date.now();
          const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
          const panoramaName = file.name
            .replace(/\.[^/.]+$/, "")
            .replace(/[^a-zA-Z0-9-_]/g, "_")
            .toLowerCase();

          const progressToast = toast({
            title: "Importing panorama",
            description: `Copying ${file.name}...`,
            progress: 0,
            duration: undefined,
          });

          try {
            // Save original file to local storage — mirror S3 path structure
            const filePath = StoragePaths.private.panoramaOriginal(
              storageSegments.craftspaceKey,
              storageSegments.projectKey,
              panoramaName,
              `${timestamp}_${safeName}`,
            );
            await localStorageSvc.uploadFile(file, filePath);
            const fileUrl = await localStorageSvc.getFileUrl(filePath);

            progressToast.update({
              title: "Importing panorama",
              description: `Generating thumbnail for ${file.name}...`,
              progress: 50,
            });

            // Generate thumbnail
            let thumbnailUrl: string | undefined;
            try {
              const { generateClientThumbnail } = await import("@/utils/clientThumbnailGenerator");
              const thumbBlob = await generateClientThumbnail(file, 1024, 512, "jpeg");
              const thumbFile = new File([thumbBlob], "thumbnail.jpg", { type: "image/jpeg" });
              const thumbPath = StoragePaths.private.thumbnail(
                storageSegments.craftspaceKey,
                storageSegments.projectKey,
                panoramaName,
                "thumbnail.jpg",
              );
              await localStorageSvc.uploadFile(thumbFile, thumbPath);
              thumbnailUrl = await localStorageSvc.getFileUrl(thumbPath);
            } catch (thumbErr) {
              console.warn("Thumbnail generation failed:", thumbErr);
            }

            // Get image dimensions
            let imgWidth: number | undefined;
            let imgHeight: number | undefined;
            try {
              const { getImageDimensions } = await import("@/utils/imageDimensions");
              const dims = await getImageDimensions(file);
              imgWidth = dims.width;
              imgHeight = dims.height;
            } catch {}

            progressToast.update({
              title: "Saving panorama",
              description: `Saving ${file.name}...`,
              progress: 90,
            });

            await createLocalPanorama({
              project_id: project.id,
              craftspace_id: project.craftspace_id || "",
              name: file.name.replace(/\.[^/.]+$/, ""),
              file_url: fileUrl,
              private_file_url: fileUrl,
              thumbnail_url: thumbnailUrl,
              width: imgWidth,
              height: imgHeight,
              order: baseOrder + fileIndex,
              file_status: "ready",
              processing_status: "idle",
              data: setPanoramaMediaType(null, mediaType),
            });

            progressToast.update({
              title: "Panorama imported",
              description: `${file.name} is ready`,
              progress: 100,
            });
          } catch (err) {
            console.error("Local import error:", err);
            progressToast.update({
              title: "Import failed",
              description: `Failed to import ${file.name}`,
              progress: 0,
            });
          }
        }
        return; // Skip cloud upload logic
      }
      // ────────────────────────────────────────────────────────────────────────

      // Get initial panorama count from database to calculate correct order values
      const existingPanoramas = (await convex.query(api.panoramas.listByProject, {
        projectId: project.id,
      })) as Panorama[];
      const baseOrder = existingPanoramas?.length || 0;

      // Track all successfully uploaded panoramas for final refresh
      const uploadedPanoramaIds: string[] = [];

      for (let fileIndex = 0; fileIndex < fileArray.length; fileIndex++) {
        const file = fileArray[fileIndex];
        // Validate file type
        if (!file.type.startsWith("image/")) {
          toast({
            variant: "destructive",
            title: "Invalid file type",
            description: `${file.name} is not an image file.`,
          });
          continue;
        }

        const timestamp = Date.now();
        const filename = `${timestamp}_${file.name}`;

        // Get the correct storage service based on craftspace configuration
        const storageProvider =
          await StorageServiceFactory.getCraftspaceStorageProvider(craftspaceId);

        let fileUrl: string;
        let compressedFileUrl: string | null = null;
        let progressToast: ReturnType<typeof toast> | null = null;

        // Client-side processing first to get compressed version
        let panoramaData: Panorama | null = null; // Declare outside try block for error handling

        try {
          const { compressPanorama } = await import("@/utils/clientPanoramaCompressor");
          const { generateClientThumbnails, uploadClientThumbnails } = await import(
            "@/utils/clientThumbnailGenerator"
          );

          const panoramaName = file.name
            .replace(/\.[^/.]+$/, "")
            .replace(/[^a-zA-Z0-9-_]/g, "_")
            .toLowerCase();

          // Show progress toast for compression
          progressToast = toast({
            title: "Compressing panorama",
            description: `Processing ${file.name}...`,
            progress: 0,
            duration: undefined, // Don't auto-dismiss
          });

          const setStageProgress = (progress: number, title: string, description: string) => {
            progressToast.update({
              title,
              description,
              progress: Math.max(0, Math.min(100, progress)),
            });
          };

          // Compress panorama first with progress tracking (skip for original-only)
          let compressionResult = null;
          if (uploadPreference !== "original-only") {
            compressionResult = await compressPanorama(file, {}, (progress) => {
              // Compression occupies 0-55%
              setStageProgress(
                (progress / 100) * 55,
                "Compressing panorama",
                `Processing ${file.name}...`,
              );
            });
          } else {
            // Skip compression for original-only, jump to start of next stage.
            setStageProgress(55, "Preparing upload", `Compression skipped for ${file.name}`);
          }

          // Update progress toast for thumbnail generation
          setStageProgress(60, "Generating thumbnails", `Creating thumbnails for ${file.name}...`);

          // Generate thumbnails
          const thumbnails = await generateClientThumbnails(file);
          const thumbnailUrls = await uploadClientThumbnails(
            thumbnails,
            finalCraftspaceId,
            project.id,
            panoramaName,
            storageSegments,
          );
          setStageProgress(75, "Uploading panorama", `Uploading ${file.name}...`);

          // Upload file based on preference
          if (uploadPreference === "original-only") {
            // Upload original file directly
            if (storageProvider === "custom-s3") {
              try {
                const originalPath = StoragePaths.private.panoramaOriginal(
                  storageSegments.craftspaceKey,
                  storageSegments.projectKey,
                  panoramaName,
                  filename,
                );
                await s3UploadFile(file, originalPath);
                fileUrl = await s3GetFileUrl(originalPath);

                // Update progress for original upload completion
                setStageProgress(
                  85,
                  "Uploading panorama",
                  `Original version uploaded for ${file.name}`,
                );
              } catch (error) {
                console.warn("Failed to upload original panorama to S3:", error);
                // Continue with processing even if S3 upload fails
              }
            } else {
              // Upload original file to cloud-managed storage
              const originalPath = StoragePaths.private.panoramaOriginal(
                storageSegments.craftspaceKey,
                storageSegments.projectKey,
                panoramaName,
                filename,
              );
              try {
                await StorageService.uploadPrivate("staging", originalPath, file, { upsert: true });
                // Get the URL for the original file
                const {
                  data: { signedUrl },
                } = await StorageService.getPrivateUrl("staging", originalPath, 86400, craftspaceId);
                fileUrl = signedUrl;

                // Update progress for original upload completion
                setStageProgress(
                  85,
                  "Uploading panorama",
                  `Original version uploaded for ${file.name}`,
                );
              } catch (upErr) {
                console.warn("Original upload warning:", upErr);
              }
            }
          } else if (compressionResult) {
            // Upload compressed file to the appropriate storage service
            if (storageProvider === "custom-s3") {
              // Upload compressed file to S3
              try {
                const compressedPath = StoragePaths.private.panoramaProcessed(
                  storageSegments.craftspaceKey,
                  storageSegments.projectKey,
                  panoramaName,
                  `compressed_${panoramaName}${compressionResult.extension}`,
                );
                // Convert Blob to File for S3 upload
                const compressedFile = new File(
                  [compressionResult.compressed],
                  `compressed_${panoramaName}${compressionResult.extension}`,
                  {
                    type: compressionResult.mimeType,
                  },
                );
                await s3UploadFile(compressedFile, compressedPath);
                compressedFileUrl = await s3GetFileUrl(compressedPath);

                // Update progress for compressed upload completion
                setStageProgress(
                  85,
                  "Uploading panorama",
                  `Compressed version uploaded for ${file.name}`,
                );
              } catch (error) {
                console.warn("Failed to upload compressed panorama to S3:", error);
                // Continue with processing even if S3 upload fails
              }
            } else {
              // Upload compressed file to cloud-managed storage
              const compressedPath = StoragePaths.private.panoramaProcessed(
                storageSegments.craftspaceKey,
                storageSegments.projectKey,
                panoramaName,
                `compressed_${panoramaName}${compressionResult.extension}`,
              );
              try {
                await StorageService.uploadPrivate(
                  "staging",
                  compressedPath,
                  compressionResult.compressed,
                  { upsert: true },
                );
                // Get the URL for the compressed file
                const {
                  data: { signedUrl },
                } = await StorageService.getPrivateUrl("staging", compressedPath, 86400, craftspaceId);
                compressedFileUrl = signedUrl;

                // Update progress for compressed upload completion
                setStageProgress(
                  85,
                  "Uploading panorama",
                  `Compressed version uploaded for ${file.name}`,
                );
              } catch (upErr) {
                console.warn("Compressed upload warning:", upErr);
              }
            }
          }

          // Handle file URL assignment based on preference
          if (uploadPreference === "compressed-only") {
            // For compressed-only mode, use the compressed file URL as the main file URL
            fileUrl = compressedFileUrl || "";
            // console.log('🔄 Using compressed version as main file for compressed-only mode');
          }
          // For original-only mode, fileUrl is already set in the upload section above

          // Validate that we have a valid file URL before proceeding
          if (!fileUrl || fileUrl.trim() === "") {
            console.error("File upload failed - no file URL available for:", file.name);
            toast({
              variant: "destructive",
              title: "Upload failed",
              description: `Failed to upload ${file.name}. The file could not be uploaded to storage. Please check your storage configuration and try again.`,
            });
            progressToast.dismiss();
            continue; // Skip creating panorama record if upload failed
          }

          // Extract image dimensions
          let imageDimensions = { width: null, height: null };
          try {
            const { getImageDimensions } = await import("@/utils/imageDimensions");
            const dimensions = await getImageDimensions(file);
            imageDimensions = { width: dimensions.width, height: dimensions.height };
            console.log(`📐 Extracted dimensions: ${dimensions.width}x${dimensions.height}`);
          } catch (error) {
            console.warn("Failed to extract image dimensions:", error);
          }

          // Extract EXIF data including GPS coordinates and orientation
          let exifData = null;
          let gpsLocation = null;
          let orientationData = null;
          try {
            const { extractExifData } = await import("@/utils/exifExtractor");
            const { exif, gps, orientation } = await extractExifData(file);

            if (exif && Object.keys(exif).length > 0) {
              exifData = exif;
            }

            // Convert EXIF GPS format {latitude, longitude} to Maps format {lat, lng} for consistency
            if (gps && gps.latitude != null && gps.longitude != null) {
              gpsLocation = {
                lat: gps.latitude,
                lng: gps.longitude,
                ...(gps.altitude != null && { altitude: gps.altitude }),
              };
              console.log(`📍 Extracted GPS coordinates: ${gpsLocation.lat}, ${gpsLocation.lng}`);
            }

            // Extract orientation data for automatic north detection
            if (orientation && orientation.heading != null) {
              orientationData = orientation;
              console.log(
                `🧭 Extracted orientation data: heading=${orientation.heading}°, pitch=${orientation.pitch ?? "N/A"}, roll=${orientation.roll ?? "N/A"}`,
              );
            }
          } catch (error) {
            console.warn("Failed to extract EXIF data:", error);
          }

          // Create panorama record with appropriate URL based on preference
          const panoramaNameClean = file.name.replace(/\.[^/.]+$/, "");
          const panoramaMetadata: Record<string, unknown> = {
            media_type: mediaType,
            ...(mediaType === "panorama"
              ? {
                  initialView: {
                    position: { x: 0, y: 0, z: 0 },
                    target: { x: 1, y: 0, z: 0 },
                    fov: 75,
                  },
                }
              : {}),
          };
          if (exifData) {
            panoramaMetadata.exif_data = exifData;
          }

          const newPanorama = {
            project_id: project.id,
            name: panoramaNameClean,
            file_url: fileUrl,
            private_file_url: fileUrl,
            file_status: "private",
            file_size:
              uploadPreference === "original-only"
                ? file.size
                : compressionResult?.compressed.size || file.size, // Use appropriate size based on preference
            width: imageDimensions.width,
            height: imageDimensions.height,
            processing_status: "processing" as const,
            order: baseOrder + fileIndex,
            data: panoramaMetadata,
          };

          // Add GPS location if available
          if (gpsLocation) {
            newPanorama.gps_location = gpsLocation;
          }

          // Auto-set north offset from orientation data if available
          // Camera heading: 0° = North, increases clockwise (90° = East, 180° = South, 270° = West)
          // North offset: the yaw value that corresponds to North (0°)
          // Since heading represents the compass direction the camera is pointing,
          // and we want yaw 0° to point North, the north offset equals the heading
          if (orientationData?.heading != null) {
            // Convert heading (0-360°, clockwise from North) to north offset
            // Heading 0° (North) → northOffset 0°
            // Heading 90° (East) → northOffset 90°
            // Heading 180° (South) → northOffset 180°
            // Heading 270° (West) → northOffset 270° or -90°
            const { normalizeAngle } = await import("@/utils/coordinateSystem");
            let northOffset = orientationData.heading;
            // Normalize to (-180, 180] range for consistency with the system
            northOffset = normalizeAngle(northOffset);

            newPanorama.data = { ...newPanorama.data, north: northOffset };
            console.log(
              `🧭 Auto-set north offset: ${northOffset.toFixed(1)}° (from camera heading: ${orientationData.heading.toFixed(1)}°)`,
            );
          }

          let insertedPanorama: Panorama | null = null;
          try {
            insertedPanorama = await convex.mutation(api.panoramas.create, newPanorama);
          } catch (insertError: unknown) {
            const message = insertError instanceof Error ? insertError.message : "Unknown error";
            console.error("Insert error:", insertError);
            toast({
              variant: "destructive",
              title: "Database error",
              description: `Failed to save ${file.name}: ${message}`,
            });
            continue;
          }

          panoramaData = insertedPanorama; // Assign to variable accessible in catch block
          uploadedPanoramaIds.push(panoramaData.id);

          // Add to store immediately (shows Processing...) - use functional update to avoid race conditions
          setPanoramas((currentPanoramas) => {
            const updated = [...currentPanoramas, panoramaData as Tables<"panoramas">];
            // Set as selected if it's the first panorama
            if (currentPanoramas.length === 0) {
              setSelectedPanorama(panoramaData);
            }
            return updated;
          });

          // Update progress toast to show completion
          setStageProgress(92, "Finalizing", `Saving ${file.name}...`);

          // Cache in IndexedDB if available
          if (isCacheInitialized) {
            try {
              // console.log('🗄️ Attempting to cache panorama and thumbnails in IndexedDB...');

              // Cache the panorama file (compressed or original based on preference)
              if (compressionResult) {
                // Cache compressed version
                await cachePanorama(
                  panoramaData.id,
                  project.id,
                  finalCraftspaceId,
                  await compressionResult.compressed.arrayBuffer(),
                  compressionResult.compressed.type,
                  panoramaName,
                );
              } else {
                // Cache original version
                await cachePanorama(
                  panoramaData.id,
                  project.id,
                  finalCraftspaceId,
                  await file.arrayBuffer(),
                  file.type,
                  panoramaName,
                );
              }

              // Cache thumbnails
              if (thumbnails.jpeg) {
                await cacheThumbnail(
                  panoramaData.id,
                  project.id,
                  finalCraftspaceId,
                  await thumbnails.jpeg.arrayBuffer(),
                  thumbnails.jpeg.type,
                  false,
                );
              }

              if (thumbnails.webp) {
                await cacheThumbnail(
                  panoramaData.id,
                  project.id,
                  finalCraftspaceId,
                  await thumbnails.webp.arrayBuffer(),
                  thumbnails.webp.type,
                  true,
                );
              }

              // console.log('✅ Successfully cached panorama and thumbnails in IndexedDB');
            } catch (cacheError) {
              console.warn("⚠️ Failed to cache in IndexedDB (continuing with upload):", cacheError);
              // Continue with upload even if caching fails
            }
          } else {
            console.log("ℹ️ IndexedDB not available - skipping cache");
          }

          // Update DB to completed + thumbnails + compression info
          const updateData: Record<string, unknown> = {
            processing_status: "completed",
            file_status: "public", // Use 'public' instead of 'processed' to match constraint
            thumbnail_url: thumbnailUrls.thumbnail_url,
            thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
            file_size: compressionResult ? compressionResult.compressed.size : file.size, // Use appropriate size
            data: compressionResult
              ? {
                  ...panoramaData.data, // Preserve existing data including initialView
                  processing_method: "client-side",
                  original_file_size: file.size,
                  compressed_file_size: compressionResult.compressed.size,
                  compression_ratio: compressionResult.compressionRatio,
                  is_compressed: true,
                }
              : {
                  ...panoramaData.data, // Preserve existing data including initialView
                  processing_method: "client-side",
                  original_file_size: file.size,
                  is_compressed: false,
                },
          };

          // For compressed-only mode, ensure we're using the compressed file URL
          if (uploadPreference === "compressed-only" && compressedFileUrl) {
            updateData.private_file_url = compressedFileUrl;
          }

          try {
            await convex.mutation(api.panoramas.update, { id: panoramaData.id, ...updateData });
          } catch (updateError) {
            console.warn("Update thumbnails warning:", updateError);
          }

          // Update store so the list reflects completion and thumbnails immediately
          const updated = {
            ...panoramaData,
            processing_status: "completed",
            file_status: "public", // Use 'public' instead of 'processed' to match constraint
            thumbnail_url: thumbnailUrls.thumbnail_url,
            thumbnail_url_webp: thumbnailUrls.thumbnail_url_webp,
            file_size: compressionResult ? compressionResult.compressed.size : file.size, // Use appropriate size
            // Update private_file_url to compressed version if in compressed-only mode
            ...(uploadPreference === "compressed-only" &&
              compressedFileUrl && { private_file_url: compressedFileUrl }),
            data: compressionResult
              ? {
                  processing_method: "client-side",
                  original_file_size: file.size,
                  compressed_file_size: compressionResult.compressed.size,
                  compression_ratio: compressionResult.compressionRatio,
                  is_compressed: true,
                }
              : {
                  processing_method: "client-side",
                  original_file_size: file.size,
                  is_compressed: false,
                },
          } as Tables<"panoramas">;

          // Update store with functional update to avoid race conditions
          setPanoramas((currentPanoramas) =>
            currentPanoramas.map((p) => (p.id === panoramaData.id ? updated : p)),
          );

          setStageProgress(96, "Finalizing", `Waiting for ${file.name} to become viewable...`);

          // Wait for actual asset availability before finishing the notification.
          try {
            const urlsToCheck = [
              updated.thumbnail_url_webp,
              updated.thumbnail_url,
              updated.private_file_url,
              updated.file_url,
            ].filter((url): url is string => !!url && typeof url === "string");
            if (urlsToCheck.length > 0) {
              await preloadImage(urlsToCheck[0], 15000);
            }
          } catch (readyErr) {
            // Non-fatal: real-time subscription/store updates may still finish moments later.
            console.warn("Asset readiness check timed out:", readyErr);
          }

          setStageProgress(100, "Upload complete", `${file.name} is ready`);
          // Dismiss only after completion is shown briefly.
          setTimeout(() => {
            progressToast.dismiss();
          }, 700);
        } catch (processingError) {
          console.error("Client-side processing error:", processingError);
          progressToast?.dismiss();

          // Only update database and store if panoramaData exists (i.e., database record was created)
          if (panoramaData) {
            // Mark as failed
            try {
              await convex.mutation(api.panoramas.update, {
                id: panoramaData.id,
                processing_status: "failed",
              });
            } catch (failErr) {
              console.warn("Failed to mark processing failed:", failErr);
            }

            // Update store
            const nowList = useEditorStore
              .getState()
              .panoramas.map((p) =>
                p.id === panoramaData.id ? { ...p, processing_status: "failed" } : p,
              );
            setPanoramas(nowList);

            toast({
              variant: "destructive",
              title: "Processing failed",
              description: `${file.name} uploaded but processing failed.`,
            });
          } else {
            // If panoramaData is null, the error occurred before database insertion
            toast({
              variant: "destructive",
              title: "Upload failed",
              description: `Failed to upload ${file.name}.`,
            });
          }
        }
      }

      // Refresh panorama list from database once after all files are processed
      // This ensures all panoramas are visible with their thumbnails
      // Add a small delay to ensure all database updates have completed
      if (uploadedPanoramaIds.length > 0) {
        // Wait a bit for all database updates to complete
        await new Promise((resolve) => setTimeout(resolve, 500));

        try {
          const refreshedPanoramas = (await convex.query(api.panoramas.listByProject, {
            projectId: project.id,
          })) as Panorama[];
          if (refreshedPanoramas) {
            // Ensure panoramas is always an array
            const panoramasArray = Array.isArray(refreshedPanoramas) ? refreshedPanoramas : [];
            setPanoramas(panoramasArray);
            console.log(`✅ Refreshed panorama list: ${panoramasArray.length} panoramas loaded`);
          }
        } catch (refreshErr) {
          console.warn("Failed to refresh panorama list after uploads:", refreshErr);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        variant: "destructive",
        title: "Upload failed",
        description: "An unexpected error occurred during upload.",
      });
    }
  };

  // Update social image to use first panorama thumbnail if no custom image is set
  const updateSocialImageIfNeeded = async (updatedPanoramas: Tables<"panoramas">[]) => {
    if (!project || updatedPanoramas.length === 0) return;

    try {
      // Check if project has a custom social image set
      const projectData = project.data as Record<string, unknown> | null;
      const hasCustomSocialImage = projectData?.socialImageUrl;

      if (hasCustomSocialImage) {
        console.log("Project has custom social image, skipping automatic update");
        return;
      }

      // Get the new first panorama
      const newFirstPanorama = updatedPanoramas[0];
      if (!newFirstPanorama || newFirstPanorama.processing_status !== "completed") {
        console.log(
          "New first panorama not ready or processing incomplete, skipping social image update",
        );
        return;
      }

      // Check if the first panorama has actually changed
      const oldFirstPanorama = panoramas[0];
      if (oldFirstPanorama && oldFirstPanorama.id === newFirstPanorama.id) {
        console.log("First panorama unchanged, skipping social image update");
        return;
      }

      console.log(
        "First panorama changed, updating social image to use new first panorama thumbnail",
      );

      // Update the project data to use the new first panorama's thumbnail
      const updatedProjectData = {
        ...projectData,
        socialImageUrl: newFirstPanorama.thumbnail_url,
      };

      // Update the project in the database
      try {
        await convex.mutation(api.projects.update, { id: project.id, data: updatedProjectData });
      } catch (updateError) {
        console.error("Failed to update project social image:", updateError);
        return;
      }

      // Update local project state
      setProject({
        ...project,
        data: updatedProjectData,
      });

      console.log("Social image updated successfully to use new first panorama thumbnail");
    } catch (error) {
      console.error("Error updating social image:", error);
    }
  };

  // Register handleUploadPanoramas into the store so other pages (e.g. Assets) can call it.
  // Use a ref to avoid re-registering on every render while always calling the latest closure.
  const uploadPanoramasRef = useRef(handleUploadPanoramas);
  uploadPanoramasRef.current = handleUploadPanoramas;
  useEffect(() => {
    useEditorStore.setState({
      uploadPanoramas: (files, pref, mediaType) =>
        uploadPanoramasRef.current(files, pref, mediaType),
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reorder panoramas implementation
  const handleReorderPanoramas = async (panoramaId: string, newOrder: number) => {
    if (!project) return;

    try {
      console.log("Reordering panorama:", { panoramaId, newOrder });

      // Get current panorama order
      const currentPanoramas = useEditorStore.getState().panoramas;
      const draggedPanorama = currentPanoramas.find((p) => p.id === panoramaId);

      if (!draggedPanorama) {
        console.error("Dragged panorama not found:", panoramaId);
        return;
      }

      const oldOrder = draggedPanorama.order;

      // Update the database
      try {
        if (projectBackend !== "local" || !isElectron()) {
          await convex.mutation(api.panoramas.update, { id: panoramaId, order: newOrder });
        }
      } catch (updateError) {
        console.error("Database update error:", updateError);
        toast({
          variant: "destructive",
          title: "Reorder failed",
          description: "Failed to save the new order to database.",
        });
        return;
      }

      // Reorder panoramas in local state
      const reorderedPanoramas = [...currentPanoramas];

      // Remove the dragged panorama from its current position
      const draggedIndex = reorderedPanoramas.findIndex((p) => p.id === panoramaId);
      if (draggedIndex !== -1) {
        const [removed] = reorderedPanoramas.splice(draggedIndex, 1);

        // Insert at new position
        if (newOrder >= reorderedPanoramas.length) {
          // Add to end
          reorderedPanoramas.push({ ...removed, order: newOrder });
        } else {
          // Insert at specific position
          reorderedPanoramas.splice(newOrder, 0, { ...removed, order: newOrder });
        }

        // Update order values for all panoramas to maintain consistency
        const updatedPanoramas = reorderedPanoramas.map((p, index) => ({
          ...p,
          order: index,
        }));

        // Update store
        setPanoramas(updatedPanoramas);

        // Persist full order in local mode so the DB matches the UI order.
        if (projectBackend === "local" && isElectron()) {
          await Promise.all(
            updatedPanoramas.map((p) => updateLocalPanorama({ id: p.id, craftspace_id: project?.craftspace_id || "", order: p.order })),
          );
        }

        // Check if the first panorama has changed and update social image if needed
        await updateSocialImageIfNeeded(updatedPanoramas);

        console.log("Reorder successful:", {
          panorama: draggedPanorama.name,
          oldOrder,
          newOrder,
        });

      }
    } catch (error) {
      console.error("Reorder error:", error);
      toast({
        variant: "destructive",
        title: "Reorder failed",
        description: "An unexpected error occurred while reordering.",
      });
    }
  };

  // Handle export button click
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  const showEditorSidePanels =
    !isSettingsRoute &&
    !isTrashRoute &&
    !isMapsRoute &&
    !isCMSRoute &&
    !isVariablesRoute &&
    !isAssetsRoute &&
    !isPreviewMode;
  const shouldRenderViewer = isSkinRoute || isEditorRoute;
  const compactPanels = windowWidth < 1280;
  const availableViewerWidth = Math.max(
    120,
    Math.floor(viewerContainerRef.current?.parentElement?.offsetWidth ?? windowWidth),
  );
  const defaultBreakpointWidth = Math.min(991, availableViewerWidth);
  const fixedViewerWidth = clampViewerWidth(viewerWidth ?? defaultBreakpointWidth);
  const activeBreakpointKey =
    viewportMode === "auto"
      ? "base"
      : BREAKPOINT_PRESETS.find(
          (preset) => preset.width !== null && Math.abs(preset.width - fixedViewerWidth) <= 8,
        )?.key;
  const selectedComponentName = selectedSkinComponent?.name?.trim().toLowerCase() ?? "";
  const isBodySelected = /^<?body>?$/.test(selectedComponentName);
  const isAnySkinComponentSelected = Boolean(selectedSkinComponent);
  const showCanvasFrame =
    isBodySelected ||
    (!isAnySkinComponentSelected && viewportMode === "fixed") ||
    isBreakpointHandleDragging;
  const showBreakpointHandle =
    showEditorSidePanels &&
    (isSkinRoute || isEditorRoute) &&
    viewportMode === "fixed" &&
    !isPreviewMode;
  const emphasizeCanvasEdges = isBreakpointHandleHovered || isBreakpointHandleDragging;

  if (authLoading || (isAuthenticated && !user)) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="flex items-center space-x-2 text-white">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-white/60 border-t-white" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // In Electron, local-only mode does not require auth.
  if (!isAuthenticated && !isElectron()) {
    return (
      <Navigate to="/login" replace />
    );
  }
  // Don't show loading screen when just switching to preview mode - keep viewer visible
  if ((localLoading || loading) && !isPreviewMode) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#111827" }}
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="relative">
            <div className="w-12 h-12 border-4 border-gray-700 border-t-blue-500 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-12 h-12 border-4 border-transparent border-t-blue-400 rounded-full animate-ping opacity-20"></div>
          </div>
          <div className="text-white text-lg font-medium">Loading project...</div>
          <div className="text-gray-400 text-sm">Please wait while we fetch your project data</div>
        </div>
      </div>
    );
  }
  if (!project && !localLoading && hasAttemptedProjectLoad) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "#111827" }}
      >
        <div className="flex flex-col items-center space-y-4 px-6 text-center">
          <div className="text-white text-lg font-medium">Project unavailable</div>
          <div className="text-gray-400 text-sm">
            Could not load this project from the current URL. Check access or retry.
          </div>
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-500 transition-colors"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <ViewportControlsProvider
      value={{ activeBreakpointKey, setViewportAuto, setViewportFixedWidth }}
    >
      <div
        className={`h-screen w-screen flex flex-col overflow-visible craftspace-container relative ${compactPanels ? "compact-panels" : ""}`}
        style={{ backgroundColor: "#111827" }}
      >
        <EditorHeader
        onExport={handleExport}
        onRefresh={handleRefresh}
        centerBottomContent={
          showBreakpointControls && showEditorSidePanels && (isSkinRoute || isEditorRoute) ? (
            <div className="flex items-center gap-1 rounded-lg border border-[#41506a] bg-[#1b273a] px-1.5 py-1 shadow-[0_6px_18px_rgba(0,0,0,0.28)]">
              {BREAKPOINT_PRESETS.map((preset) =>
                preset.width === null ? (
                  <Tooltip key={preset.key}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="Base breakpoint"
                        className={`min-w-[28px] px-2 py-[3px] rounded-md text-[10px] font-medium leading-none border whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70 ${
                          activeBreakpointKey === preset.key
                            ? "bg-slate-500/30 border-slate-400/80 text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
                            : "bg-transparent border-transparent text-gray-300 hover:bg-[#2d3d56] hover:text-gray-100"
                        }`}
                        onClick={() => {
                          setViewportAuto();
                        }}
                      >
                        <Star
                          className={`mx-auto h-3.5 w-3.5 ${
                            activeBreakpointKey === preset.key ? "fill-current" : ""
                          }`}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="border-gray-600 bg-gray-800 text-white max-w-[240px] px-3 py-2"
                    >
                      <div className="text-[11px] font-semibold text-white">Base</div>
                      <p className="mt-1 text-[11px] leading-4 text-gray-200">
                        Styles on Base apply to all viewport sizes unless overridden by another
                        breakpoint.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Tooltip key={preset.key}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label={`${preset.label}px breakpoint`}
                        className={`min-w-[28px] px-2 py-[3px] rounded-md text-[10px] font-medium leading-none border whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400/70 ${
                          activeBreakpointKey === preset.key
                            ? "bg-slate-500/30 border-slate-400/80 text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]"
                            : "bg-transparent border-transparent text-gray-300 hover:bg-[#2d3d56] hover:text-gray-100"
                        }`}
                        onClick={() => {
                          setViewportFixedWidth(preset.width);
                        }}
                      >
                        {preset.icon ? (
                          <preset.icon className="mx-auto h-3.5 w-3.5" />
                        ) : (
                          preset.label
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      className="border-gray-600 bg-gray-800 px-2 py-1 text-[11px] text-white"
                    >
                      {preset.label}px
                    </TooltipContent>
                  </Tooltip>
                ),
              )}
            </div>
          ) : undefined
        }
      />
      {!isPreviewMode && <VerticalNav />}
      {/* Main container with panels and viewer */}
      <div className={`flex-1 flex overflow-visible ${!isPreviewMode ? "ml-12" : ""}`}>
        {/* Left Panel - Scene Hierarchy or Component Library */}
        {showEditorSidePanels && (
          <div
            className="w-[320px] bg-gray-800 border-r border-gray-700 flex flex-col panel-container"
            style={compactPanels ? { fontSize: "13px", lineHeight: 1.25 } : undefined}
          >
            {isSkinRoute ? (
              <ComponentLibrary
                components={skinComponents}
                selectedComponent={selectedSkinComponent}
                onComponentSelect={setSelectedSkinComponent}
                onCreateComponent={addSkinComponent}
                onDeleteComponent={deleteSkinComponent}
                onReorderComponents={reorderSkinComponents}
                onNestComponent={nestSkinComponent}
                onUpdateComponent={updateSkinComponent}
              />
            ) : (
              <SceneHierarchy
                uploadPanoramasImpl={handleUploadPanoramas}
                reorderPanoramasImpl={handleReorderPanoramas}
              />
            )}
          </div>
        )}

        {/* 360 Viewer with resizable width - only shown on editor/skin routes */}
        {shouldRenderViewer && (
          <div className="flex-1 flex flex-col items-center min-h-0 relative">
            <div className="flex-1 min-h-0 w-full flex justify-center relative overflow-visible">
              {showBreakpointHandle ? (
                <div
                  className="pointer-events-none absolute left-3 top-1/2 z-[2550] -translate-y-1/2 rounded bg-[#2b2b2b] px-1 py-0.5 text-[10px] text-gray-300"
                  style={{
                    writingMode: "vertical-rl",
                    transform: "translateY(-50%) rotate(180deg)",
                  }}
                >
                  canvas (max-width: {Math.round(fixedViewerWidth)}px)
                </div>
              ) : null}
              <div
                className="relative h-full viewer-container group/canvas z-[2000]"
                style={{
                  width:
                    isPreviewMode || ((isSkinRoute || isEditorRoute) && viewportMode === "auto")
                      ? "100%"
                      : viewerWidth
                        ? `${viewerWidth}px`
                        : "100%",
                  minWidth: isPreviewMode ? "0px" : compactPanels ? "200px" : "220px",
                  flex: isPreviewMode ? "1" : "0 0 auto",
                  willChange: "width", // Optimize for width changes
                }}
                ref={viewerContainerRef}
              >
                <div className="absolute inset-0 overflow-visible">
                  {getPanoramaMediaType(selectedPanorama) === "zoomable_image" ? (
                    <ZoomableImageViewer />
                  ) : (
                    <Viewer360 />
                  )}
                </div>

                {(isSkinRoute || isBreakpointHandleDragging) && showCanvasFrame ? (
                  <div className="pointer-events-none absolute inset-0 z-[2500]">
                    <div
                      className="absolute top-0 bottom-0 left-0 bg-blue-500 transition-all duration-150"
                      style={{ width: emphasizeCanvasEdges || isBreakpointHandleDragging ? "3px" : "1px" }}
                    />
                    <div
                      className="absolute top-0 bottom-0 right-0 bg-blue-500 transition-all duration-150"
                      style={{ width: emphasizeCanvasEdges || isBreakpointHandleDragging ? "3px" : "1px" }}
                    />
                    <div className="absolute top-0 left-0 right-0 h-px bg-blue-500/80" />
                    <div className="absolute bottom-0 left-0 right-0 h-px bg-blue-500/80" />
                    {isBodySelected ? (
                      <div className="absolute top-1 left-1 rounded bg-blue-600 px-1 py-0.5 text-[10px] leading-none text-white opacity-0 transition-opacity duration-150 group-hover/canvas:opacity-100">
                        {"<body>"}
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {showBreakpointHandle ? (
                  // biome-ignore lint/a11y/noStaticElementInteractions: breakpoint resize handle with mouse-only drag
                  <div
                    className="absolute top-1/2 right-[-30px] z-[2550] -translate-y-1/2 cursor-ew-resize"
                    onDoubleClick={() => {
                      if (isPreviewMode) return;
                      setViewportAuto();
                    }}
                    onMouseEnter={() => setIsBreakpointHandleHovered(true)}
                    onMouseLeave={() => setIsBreakpointHandleHovered(false)}
                    onMouseDown={(e) => {
                      if (isPreviewMode) return;

                      document.body.style.userSelect = "none";
                      (document.body.style as unknown as Record<string, string>).webkitUserSelect =
                        "none";

                      setIsBreakpointHandleDragging(true);

                      const container = viewerContainerRef.current;
                      if (!container) return;

                      const startWidth = container.offsetWidth;
                      const startX = e.pageX;
                      const viewer = container.querySelector("canvas");
                      if (!viewer) return;

                      const startAspect = viewer.clientWidth / Math.max(viewer.clientHeight, 1);

                      const onMouseMove = (event: MouseEvent) => {
                        const delta = event.pageX - startX;
                        const newWidth = startWidth + delta * 2;
                        const finalWidth = clampViewerWidth(newWidth);
                        container.style.width = `${finalWidth}px`;

                        const windowWithThrottle = window as Window & { _resizeThrottle?: boolean };
                        if (!windowWithThrottle._resizeThrottle) {
                          windowWithThrottle._resizeThrottle = true;
                          requestAnimationFrame(() => {
                            const newAspect = viewer.clientWidth / Math.max(viewer.clientHeight, 1);
                            const resizeEvent = new CustomEvent("viewer-resize", {
                              detail: { startAspect, newAspect },
                            });
                            window.dispatchEvent(resizeEvent);
                            windowWithThrottle._resizeThrottle = false;
                          });
                        }
                      };

                      const onMouseUp = () => {
                        const finalWidth = container.offsetWidth;
                        setViewportFixedWidth(finalWidth);
                        setIsBreakpointHandleDragging(false);

                        document.body.style.userSelect = "";
                        (
                          document.body.style as unknown as Record<string, string>
                        ).webkitUserSelect = "";

                        document.removeEventListener("mousemove", onMouseMove);
                        document.removeEventListener("mouseup", onMouseUp);
                      };

                      document.addEventListener("mousemove", onMouseMove);
                      document.addEventListener("mouseup", onMouseUp);
                    }}
                  >
                    <div className="flex h-16 w-[30px] items-center justify-center rounded-sm rounded-tl-none rounded-bl-none border border-transparent bg-[#2b2b2b]">
                      <div className="mx-auto flex h-[36px] w-[10px] items-center justify-center gap-[4px]">
                        <span className="h-full w-[2px] rounded bg-white/80" />
                        <span className="h-full w-[2px] rounded bg-white/80" />
                      </div>
                    </div>
                  </div>
                ) : null}

              </div>
            </div>
          </div>
        )}

        {/* Maps page - full width overlay */}
        {isMapsRoute && !isPreviewMode ? (
          <div
            className="absolute overflow-auto z-30 maps-overlay"
            style={{
              top: "57px",
              left: "48px",
              right: "0",
              bottom: "0",
              backgroundColor: "#1F2937",
            }}
          >
            <Outlet />
          </div>
        ) : null}

        {/* Right Panel - Properties/Settings/Skin */}
        {showEditorSidePanels ? (
          <div
            className="w-[320px] bg-gray-800 border-l border-gray-700 panel-container panel-container-right relative flex flex-col"
            style={compactPanels ? { fontSize: "13px", lineHeight: 1.25 } : undefined}
          >
            <div className="flex-1 min-h-0">
              <Outlet />
            </div>
          </div>
        ) : isSettingsRoute && !isPreviewMode ? (
          <div
            className="absolute overflow-auto z-30 settings-overlay"
            style={{
              top: "57px",
              left: "48px",
              right: "0",
              bottom: "0",
              backgroundColor: "#1F2937",
            }}
          >
            <Outlet />
          </div>
        ) : isTrashRoute && !isPreviewMode ? (
          <div
            className="absolute overflow-auto z-30 trash-overlay"
            style={{
              top: "57px",
              left: "48px",
              right: "0",
              bottom: "0",
              backgroundColor: "#1F2937",
            }}
          >
            <Outlet />
          </div>
        ) : isCMSRoute && !isPreviewMode ? (
          <div
            className="absolute overflow-auto z-30 cms-overlay"
            style={{
              top: "57px",
              left: "48px",
              right: "0",
              bottom: "0",
              backgroundColor: "#1F2937",
            }}
          >
            <Outlet />
          </div>
        ) : isAssetsRoute && !isPreviewMode ? (
          <div
            className="absolute overflow-auto z-30 assets-overlay"
            style={{
              top: "57px",
              left: "48px",
              right: "0",
              bottom: "0",
              backgroundColor: "#1F2937",
            }}
          >
            <Outlet />
          </div>
        ) : isVariablesRoute && !isPreviewMode ? (
          <div
            className="absolute overflow-auto z-30 variables-overlay"
            style={{
              top: "57px",
              left: "48px",
              right: "0",
              bottom: "0",
              backgroundColor: "#1F2937",
            }}
          >
            <Outlet />
          </div>
        ) : null}
      </div>

      {project && exportDialogOpen && (
        <ExportDialog
          onClose={() => setExportDialogOpen(false)}
          project={project}
          panoramas={panoramas}
          hotspots={hotspots}
        />
      )}

      <Dialog open={backupDialogOpen} onOpenChange={setBackupDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Create Backup</DialogTitle>
            <DialogDescription className="text-gray-400">
              Name this backup so you can recognize it later.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={backupName}
            onChange={(e) => setBackupName(e.target.value)}
            placeholder="Backup name..."
            className="bg-gray-700 border-gray-600 text-white"
            onKeyDown={(e) => e.key === "Enter" && handleCreateBackup()}
          />
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              className="text-gray-300 hover:text-white hover:bg-gray-700"
              onClick={() => setBackupDialogOpen(false)}
              disabled={backupSaving}
            >
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleCreateBackup}
              disabled={backupSaving || !backupName.trim()}
            >
              {backupSaving ? "Saving..." : "Save Backup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ViewportControlsProvider>
  );
};

export default ProjectCraftspace;
